import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { formatVehicleName } from "@/utils/vehicleName";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Loader2, Car, ShieldCheck, AlertCircle, ClipboardList,
  Wrench, Phone, Mail, Clock, ChevronRight, ExternalLink, FileSearch,
} from "lucide-react";
import { API } from "@/contexts/AuthContext";
import Header from "@/components/shared/Header";
import MOTWorkflowControlCard from "@/components/intake/MOTWorkflowControlCard";

/**
 * FOH Job View — calm landing for "Job card created — open".
 *
 * Route: /app/foh/job/:id
 *
 * Replaces the pre-existing dead route that fell through to the marketing
 * homepage. Single-focus surface: shows the freshly-handed-over job card
 * with the FOH-relevant context and one-tap navigation to the deeper
 * surfaces (Intake session, MOT workspace, Technician VI).
 *
 * Architectural choice: this page READS only. The FOH actions live on the
 * deeper surfaces (intake, workshop, MOT). Keeping this page thin avoids
 * duplicating action affordances.
 */
export default function FOHJobView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [motOutcome, setMotOutcome] = useState(null);
  const [motWorkflow, setMotWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buildingYestimate, setBuildingYestimate] = useState(false);
  const [buildError, setBuildError] = useState(null);

  // ---- helpers -------------------------------------------------------------
  // Re-fetch the outcome doc after a baseline-import side-effect.
  const refetchOutcome = async () => {
    try {
      const r = await axios.get(`${API}/jobs/${id}/mot-outcome`);
      if (r?.data?.outcome) setMotOutcome(r.data);
      return r?.data;
    } catch {
      return null;
    }
  };

  /**
   * Orchestrated "Price MOT Results" flow. Handles three scenarios so a
   * stuck job (e.g. MW16WPN where the DVSA poll never advanced) can be
   * unstuck with one tap:
   *
   *   1. MOT outcome already exists with defects → create-yestimate →
   *      navigate to pricing.
   *   2. No outcome yet, but the workflow baseline carries a real DVSA
   *      ``test_number`` → import-baseline-as-outcome (synthesises the
   *      outcome from the latest DVSA history), then create-yestimate,
   *      then navigate.
   *   3. Neither outcome nor baseline → surface a clear "Check DVSA first"
   *      hint pointing the FOH at the workflow card below.
   *
   * Idempotent — the import + create endpoints both no-op on repeat calls,
   * so double-clicks land on the same yestimate.
   */
  const openOrCreateYestimate = async () => {
    if (!id) return;
    setBuildError(null);
    setBuildingYestimate(true);
    try {
      let outcomeReady = !!motOutcome?.outcome;
      let importedDefectCount = (motOutcome?.outcome?.defects || []).length;

      // Step 1: if no outcome on file, try to mint one from the baseline.
      if (!outcomeReady) {
        const sid = job?.intake_session_id;
        const baselineTestNumber =
          motWorkflow?.baseline?.test_number || null;
        if (!sid || !baselineTestNumber) {
          setBuildError(
            "No MOT result on file yet — click \"Check DVSA MOT Result\" below first, then retry.",
          );
          setBuildingYestimate(false);
          return;
        }
        try {
          const imp = await axios.post(
            `${API}/intake/sessions/${sid}/mot-workflow/import-baseline-as-outcome`,
            { reason: "FOH price-defects shortcut from job view" },
          );
          importedDefectCount = imp?.data?.result?.defect_count ?? 0;
        } catch (impErr) {
          const detail = impErr?.response?.data?.detail;
          setBuildError(
            detail ||
              "Could not import the MOT result from DVSA — try Check DVSA below.",
          );
          setBuildingYestimate(false);
          return;
        }
        // Refresh the outcome so the rest of the page repaints.
        const fresh = await refetchOutcome();
        outcomeReady = !!fresh?.outcome;
        if (fresh?.outcome) {
          importedDefectCount = (fresh.outcome.defects || []).length;
        }
        if (!outcomeReady) {
          setBuildError(
            "MOT result imported but the outcome failed to materialise — refresh and retry.",
          );
          setBuildingYestimate(false);
          return;
        }
      }

      // Clean-pass guard — if the imported outcome has zero defects there
      // is nothing to price. Surface a friendly hint instead of letting
      // create-yestimate return a 400.
      if (importedDefectCount <= 0) {
        setBuildError(
          "MOT result imported — clean pass with no defects. Use \"Confirm pass · archive\" on the dashboard.",
        );
        setBuildingYestimate(false);
        return;
      }

      // Step 2: create / resolve the yestimate (idempotent server-side).
      const r = await axios.post(`${API}/jobs/${id}/mot-outcome/create-yestimate`);
      const yid = r?.data?.yestimate_id;
      if (yid) {
        navigate(`/app/foh/yestimate/${yid}/pricing`);
      } else {
        setBuildError("Yestimate created but no id returned — refresh and retry.");
      }
    } catch (err) {
      setBuildError(err?.response?.data?.detail || "Could not open the pricing surface.");
    } finally {
      setBuildingYestimate(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API}/jobs/${id}`);
        setJob(r.data);
        // Best-effort workflow snapshot — needed so the orchestrated
        // "Price MOT Results" CTA knows whether a baseline-import is
        // viable on this stuck session.
        const sid = r.data?.intake_session_id;
        if (sid) {
          try {
            const wf = await axios.get(`${API}/intake/sessions/${sid}/mot-workflow`);
            setMotWorkflow(wf.data);
          } catch {
            // Non-fatal — the card simply falls back to outcome-only mode.
          }
        }
      } catch (err) {
        setError(err?.response?.data?.detail || "Could not load this job.");
      }
      try {
        const r2 = await axios.get(`${API}/jobs/${id}/mot-outcome`);
        if (r2?.data?.outcome) setMotOutcome(r2.data);
      } catch {
        // No outcome yet — fine, panel just won't render.
      }
      setLoading(false);
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-center text-slate-500">
          <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
          Loading job card…
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-2xl mx-auto p-8">
          <div
            className="rounded-md border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-2"
            data-testid="foh-job-error"
          >
            <AlertCircle className="w-4 h-4 text-red-700 mt-0.5" />
            <p className="text-[14px] text-red-900">{error || "Job not found."}</p>
          </div>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate("/app/foh")}
            data-testid="foh-job-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to FOH dashboard
          </Button>
        </div>
      </div>
    );
  }

  const isMotJob = isMotFamily(job);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-4xl mx-auto px-5 sm:px-6 py-6" data-testid="foh-job-view">
        <button
          onClick={() => navigate(-1)}
          className="text-slate-500 hover:text-slate-800 inline-flex items-center text-[14px] mb-4"
          data-testid="foh-job-back-link"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </button>

        {/* Header strip */}
        <div
          className="rounded-xl border border-slate-200 bg-white px-6 py-5 mb-5"
          data-testid="foh-job-header"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div
                className="inline-flex items-center justify-center bg-yellow-300 border-2 border-slate-900 px-4 py-2 rounded-md font-mono font-bold tracking-wider text-slate-900 text-[20px] shadow-sm"
                data-testid="foh-job-reg-plate"
              >
                <Car className="w-4 h-4 mr-2 opacity-60" />
                {(job.reg || "—").toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  Job card
                </p>
                <h1 className="text-[22px] font-bold text-slate-900 leading-tight" data-testid="foh-job-customer-name">
                  {job.customer_name || job.customer || "—"}
                </h1>
                <p className="text-[14px] text-slate-500 mt-0.5">
                  {[formatVehicleName(job.make, job.model), job.year].filter(Boolean).join(" ") || "Vehicle details to confirm"}
                </p>
              </div>
            </div>
            {job.job_sheet_number && (
              <div
                className="text-right"
                data-testid="foh-job-sheet-number"
              >
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Job sheet</p>
                <p className="text-[18px] font-mono font-bold text-slate-800">{job.job_sheet_number}</p>
              </div>
            )}
          </div>
          {/* Quick meta row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-slate-100">
            <MetaCell icon={<Phone className="w-3.5 h-3.5" />} label="Contact" value={job.customer_phone || job.contact_number} testid="foh-job-phone" />
            <MetaCell icon={<Mail className="w-3.5 h-3.5" />} label="Email" value={job.customer_email} testid="foh-job-email" />
            <MetaCell icon={<ClipboardList className="w-3.5 h-3.5" />} label="Mileage" value={job.mileage ? `${Number(job.mileage).toLocaleString()} mi` : null} testid="foh-job-mileage" />
            <MetaCell icon={<Wrench className="w-3.5 h-3.5" />} label="Key tag" value={job.key_tag || job.key_tag_number} testid="foh-job-key-tag" mono />
          </div>
        </div>

        {/* FOH MOT workflow control — embedded for MOT jobs only. Exposes
            "Resolve manually" + slot booking + import state, so when FOH
            clicks a "MOT results requiring action" card from the dashboard
            they land directly on the actions they need (manual resolve,
            archive, retry) instead of being bounced to tech surfaces.
            Reuses the same component the tester sees so the workflow
            state never drifts between the two roles. */}
        {isMotJob && job.intake_session_id && (
          <section
            className="mb-5"
            data-testid="foh-job-mot-workflow"
            onClick={(e) => e.stopPropagation()}
          >
            <MOTWorkflowControlCard
              sessionId={job.intake_session_id}
              onImported={() => {
                // Refresh the job + outcome so the rest of the page
                // re-paints with the freshly imported result.
                axios.get(`${API}/jobs/${id}`).then((r) => setJob(r.data)).catch(() => {});
                axios.get(`${API}/jobs/${id}/mot-outcome`).then((r) => {
                  if (r?.data?.outcome) setMotOutcome(r.data);
                }).catch(() => {});
                axios.get(`${API}/intake/sessions/${job.intake_session_id}/mot-workflow`)
                  .then((r) => setMotWorkflow(r.data)).catch(() => {});
              }}
            />
          </section>
        )}

        {/* Orphan / stuck-workflow rescue CTA -----------------------------
            Visible for MOT jobs whenever a) no outcome has been imported
            yet but DVSA shows a real baseline test (MW16WPN case — DVSA
            poll never advanced), OR b) outcome exists but the MOT
            outcome card lower down has no actionable defects (rare). One
            tap triggers import-baseline-as-outcome + create-yestimate so
            FOH never has to hunt for the right surface.                  */}
        {isMotJob && !motOutcome?.outcome && motWorkflow?.baseline?.test_number && (
          <section
            className="rounded-xl border-2 border-blue-300 bg-blue-50 px-6 py-5 mb-5"
            data-testid="foh-job-orphan-price-card"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <p className="text-[12px] uppercase tracking-[0.18em] text-blue-700 font-semibold flex items-center gap-1.5">
                  <FileSearch className="w-3.5 h-3.5" />
                  MOT result detected
                </p>
                <p className="text-[16px] font-semibold text-blue-900 mt-1 leading-snug">
                  DVSA has test{" "}
                  <span className="font-mono">
                    #{motWorkflow.baseline.test_number}
                  </span>{" "}
                  ({motWorkflow.baseline.test_result || "—"}) on file for this vehicle.
                </p>
                <p className="text-[13.5px] text-blue-800 mt-1 leading-snug">
                  Import it and jump straight to pricing — one tap.
                </p>
              </div>
              <div className="flex flex-col items-stretch gap-2">
                <Button
                  size="sm"
                  onClick={openOrCreateYestimate}
                  disabled={buildingYestimate}
                  className="bg-blue-700 hover:bg-blue-800 text-white text-[13px] h-10 px-4"
                  data-testid="foh-job-price-mot-results-btn"
                >
                  {buildingYestimate ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      Opening pricing…
                    </>
                  ) : (
                    <>
                      Price MOT results <ChevronRight className="w-3.5 h-3.5 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </div>
            {buildError && (
              <p
                className="text-[12.5px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 mt-3"
                data-testid="foh-job-orphan-price-error"
              >
                {buildError}
              </p>
            )}
          </section>
        )}

        {/* Intent / scope */}
        {(job.vehicle_here_for || job.intake_triage_path_labels?.length || job.diagnostic_request_text) && (
          <section
            className="rounded-xl border border-slate-200 bg-white px-6 py-5 mb-5"
            data-testid="foh-job-scope"
          >
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
              Booked for
            </p>
            {job.vehicle_here_for && (
              <p className="text-[17px] font-bold text-slate-900 mb-1">{job.vehicle_here_for}</p>
            )}
            {Array.isArray(job.intake_triage_path_labels) && job.intake_triage_path_labels.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {job.intake_triage_path_labels.map((lbl, i) => (
                  <span
                    key={i}
                    className="text-[11.5px] uppercase tracking-wide bg-slate-100 text-slate-700 rounded px-2 py-0.5 font-semibold"
                    data-testid={`foh-job-triage-label-${i}`}
                  >
                    {lbl}
                  </span>
                ))}
              </div>
            )}
            {job.diagnostic_request_text && (
              <div className="mt-3 rounded-md bg-slate-50/70 border border-slate-200 px-3.5 py-2.5">
                <p className="text-[11.5px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
                  Customer concern at booking
                </p>
                <p className="text-[14.5px] text-slate-700 italic leading-snug">
                  "{job.diagnostic_request_text}"
                </p>
              </div>
            )}
          </section>
        )}

        {/* Status + workshop state */}
        <section
          className="rounded-xl border border-slate-200 bg-white px-6 py-5 mb-5"
          data-testid="foh-job-status"
        >
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
            Workshop state
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <StatusPill status={job.status} />
            {job.workflow_stage && (
              <span className="text-[12px] uppercase tracking-wide bg-slate-100 text-slate-700 rounded-full px-2.5 py-0.5 font-semibold">
                {job.workflow_stage.replace(/_/g, " ")}
              </span>
            )}
            {job.created_at && (
              <span className="text-[12.5px] text-slate-400 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> Booked in {formatDateTime(job.created_at)}
              </span>
            )}
          </div>
        </section>

        {/* MOT outcome — only if recorded already */}
        {motOutcome?.outcome && (
          <section
            className={`rounded-xl border px-6 py-5 mb-5 ${
              motOutcome.outcome.test_result === "FAILED"
                ? "border-red-200 bg-red-50/40"
                : "border-emerald-200 bg-emerald-50/30"
            }`}
            data-testid="foh-job-mot-outcome-card"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  MOT outcome
                </p>
                <p
                  className={`text-[24px] font-bold leading-none mt-1 ${
                    motOutcome.outcome.test_result === "FAILED" ? "text-red-700" : "text-emerald-700"
                  }`}
                  data-testid="foh-job-mot-result"
                >
                  {motOutcome.outcome.test_result}
                </p>
              </div>
              <div className="flex flex-col items-stretch gap-2">
                {/* Primary FOH action: open / create the yestimate so the
                    defects can be priced. Idempotent against the
                    create-yestimate endpoint — if one already exists this
                    just navigates straight to it. */}
                {(motOutcome.outcome.defects || []).some(
                  (d) => (d.severity || "").toLowerCase() !== "user_entered",
                ) && (
                  <Button
                    size="sm"
                    onClick={openOrCreateYestimate}
                    disabled={buildingYestimate}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-[13px]"
                    data-testid="foh-job-price-defects-btn"
                  >
                    {buildingYestimate ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        Opening pricing…
                      </>
                    ) : (
                      <>
                        Price defects <ChevronRight className="w-3.5 h-3.5 ml-1" />
                      </>
                    )}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/app/tech/mot/${id}`)}
                  className="text-[13px]"
                  data-testid="foh-job-open-mot-workspace-btn"
                >
                  Open MOT workspace <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            </div>
            {buildError && (
              <p
                className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-2"
                data-testid="foh-job-price-defects-error"
              >
                {buildError}
              </p>
            )}
            {motOutcome.decision_summary?.counts?.unactioned > 0 && (
              <p className="text-[13.5px] text-amber-800 mt-1.5">
                {motOutcome.decision_summary.counts.unactioned} defect(s) awaiting your pricing decision.
              </p>
            )}
          </section>
        )}

        {/* Action surfaces — calm grid */}
        <section
          className="rounded-xl border border-slate-200 bg-white px-6 py-5"
          data-testid="foh-job-actions"
        >
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">
            Open in…
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {job.intake_session_id && (
              <ActionCard
                icon={<ClipboardList className="w-4 h-4" />}
                title="Intake session"
                subtitle="The original FOH conversation"
                onClick={() => navigate(`/app/foh/yestimate-intake/${job.intake_session_id}`)}
                testid="foh-job-action-intake"
              />
            )}
            {isMotJob && (
              <ActionCard
                icon={<ShieldCheck className="w-4 h-4 text-teal-600" />}
                title="MOT workspace"
                subtitle="Tester pre-test + outcome capture"
                onClick={() => navigate(`/app/tech/mot/${id}`)}
                testid="foh-job-action-mot"
              />
            )}
            <ActionCard
              icon={<Wrench className="w-4 h-4" />}
              title="Vehicle inspection"
              subtitle="Technician VI sections"
              onClick={() => navigate(`/app/tech/job/${id}/sections`)}
              testid="foh-job-action-vi"
            />
            <ActionCard
              icon={<ExternalLink className="w-4 h-4" />}
              title="FOH dashboard"
              subtitle="Back to the daily board"
              onClick={() => navigate("/app/foh")}
              testid="foh-job-action-dashboard"
            />
          </div>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers + atoms
// ---------------------------------------------------------------------------

function isMotFamily(job) {
  if (!job) return false;
  const vhf = (job.vehicle_here_for || "").toLowerCase();
  if (vhf.includes("mot")) return true;
  const labels = (job.intake_triage_path_labels || []).map((l) => l.toLowerCase());
  return labels.some((l) => l.includes("mot"));
}

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const tones = {
    pending:      "bg-slate-100 text-slate-700 border-slate-200",
    in_progress:  "bg-blue-50 text-blue-800 border-blue-200",
    completed:    "bg-emerald-50 text-emerald-800 border-emerald-200",
    declined:     "bg-red-50 text-red-800 border-red-200",
    archived:     "bg-slate-50 text-slate-500 border-slate-200",
  };
  const tone = tones[s] || tones.pending;
  return (
    <span
      className={`text-[12px] uppercase tracking-wide font-semibold border rounded-full px-2.5 py-0.5 ${tone}`}
      data-testid="foh-job-status-pill"
    >
      {s.replace(/_/g, " ") || "Pending"}
    </span>
  );
}

function MetaCell({ icon, label, value, testid, mono }) {
  return (
    <div data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold inline-flex items-center gap-1">
        {icon} {label}
      </p>
      <p className={`text-[13.5px] text-slate-800 mt-0.5 leading-snug ${mono ? "font-mono" : ""}`}>
        {value || <span className="text-slate-400 italic font-sans">—</span>}
      </p>
    </div>
  );
}

function ActionCard({ icon, title, subtitle, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-lg border border-slate-200 hover:border-slate-400 bg-white px-4 py-3 transition-colors flex items-center justify-between gap-3"
      data-testid={testid}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {icon}
          <p className="text-[15px] font-semibold text-slate-900">{title}</p>
        </div>
        <p className="text-[12.5px] text-slate-500 mt-0.5 leading-snug">{subtitle}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
    </button>
  );
}

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
      + " · "
      + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
